import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Grid3X3,
  List,
  Volume2,
  Play,
  Pause,
  Star,
  StarOff,
  ExternalLink,
  Plus,
  Calendar,
  Loader2,
  ChevronDown,
  ChevronUp,
  Edit2
} from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { toggleStoryFeatured } from '../utils/story_management';
import '../styles/ArtSpeaksDirectory.css';

const ITEMS_PER_PAGE = 18;

const contentTypeOptions = [
  { value: 'all', label: 'All Content' },
  { value: 'story', label: 'Art Speaks Stories' },
  { value: 'audio', label: 'Audio Artworks' }
];

const storyTypeOptions = [
  { value: 'all', label: 'All Story Types' },
  { value: 'inspiration', label: 'Inspiration' },
  { value: 'process', label: 'Process' },
  { value: 'meaning', label: 'Meaning' },
  { value: 'personal', label: 'Personal' },
  { value: 'technique', label: 'Technique' },
  { value: 'collaboration', label: 'Collaboration' }
];

const sortOptions = [
  { value: 'created_at', label: 'Newest' },
  { value: 'title', label: 'Title' },
  { value: 'artist', label: 'Artist' }
];

const deriveName = (person) => {
  if (!person) return '';
  if (person.nickname) return person.nickname;
  const combined = `${person.first_name || ''} ${person.last_name || ''}`.trim();
  if (combined) return combined;
  if (person.handle) return person.handle;
  return '';
};

const truncateText = (text, limit = 180) => {
  if (!text) return '';
  if (text.length <= limit) return text;
  return `${text.slice(0, limit)}…`;
};

const formatTime = (seconds) => {
  if (!seconds || Number.isNaN(seconds)) return '0:00';
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const normalizeStory = (story) => {
  const writerName = deriveName(story.story_writer);
  const visualArtistName = deriveName(story.artwork?.visual_artist);
  const artworkTitle = story.artwork?.title || '';

  return {
    id: `story-${story.story_id}`,
    rawId: story.story_id,
    type: 'story',
    title: story.title,
    storyType: story.story_type,
    storyText: story.story_text,
    featured: story.featured,
    createdAt: story.created_at,
    updatedAt: story.updated_at,
    audioUrl: story.audio_url,
    videoUrl: story.video_url,
    coverImage: story.cover_image || story.artwork?.cover_image || '',
    artworkTitle,
    artworkId: story.vc_id,
    writerName,
    visualArtistName,
    eventName: story.event?.name || story.event?.title || '',
    eventDate: story.event?.event_start,
    visibility: story.visibility,
    publishedAt: story.published_at,
    contentWarnings: story.content_warnings,
    clipCount: story.audio_url ? 1 : 0
  };
};

const normalizeAudioArtwork = (artwork) => ({
  id: `audio-${artwork.id || artwork.vc_id}`,
  rawId: artwork.id || artwork.vc_id,
  type: 'audio',
  title: artwork.title,
  artistName: artwork.artist_name,
  createdAt: artwork.created_at,
  updatedAt: artwork.updated_at,
  coverImage: artwork.cover_image || '',
  description: artwork.description,
  hasFeaturedAudio: artwork.has_featured_audio,
  audioClips: artwork.audio_clips || [],
  audioCount: artwork.audio_count || (artwork.audio_clips ? artwork.audio_clips.length : 0),
  artworkId: artwork.vc_id || artwork.id,
  price: artwork.price,
  dimensions: artwork.dimensions,
  collectionCount: artwork.collection_count,
  eventName: artwork.event_name || ''
});

const ArtSpeaksDirectory = () => {
  const navigate = useNavigate();
  const audioRefs = useRef({});
  const [user, setUser] = useState(null);
  const [userDetails, setUserDetails] = useState(null);
  const [userDetailsLoading, setUserDetailsLoading] = useState(true);
  const [preferences, setPreferences] = useState({});
  const isAdmin = Boolean(userDetails?.isAdmin);
  const preferencesAppliedRef = useRef(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allItems, setAllItems] = useState([]);
  const [displayItems, setDisplayItems] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [refreshKey, setRefreshKey] = useState(0);

  // Check auth and fetch user details on mount
  useEffect(() => {
    const initializeUser = async () => {
      try {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        setUser(authUser);

        if (authUser) {
          // Use the RPC function to get user details with proper admin check
          const { data, error } = await supabase.rpc('get_user_details_basic', {
            user_uuid: authUser.id
          });

          if (error) {
            console.error('Error fetching user details:', error);
          } else if (data?.user_details) {
            const details = data.user_details;
            
            setUserDetails({ 
              isAdmin: details.isAdmin === true,
              ...details
            });
            
            if (details.preferences) {
              setPreferences(details.preferences);
            }
          }
        }
      } catch (error) {
        console.error('Error initializing user:', error);
      } finally {
        setUserDetailsLoading(false);
      }
    };

    initializeUser();
  }, []);

  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [contentFilter, setContentFilter] = useState('all');
  const [storyTypeFilter, setStoryTypeFilter] = useState('all');
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);

  const [playingKey, setPlayingKey] = useState(null);
  const [audioProgress, setAudioProgress] = useState({});
  const [audioDuration, setAudioDuration] = useState({});
  const [togglingStories, setTogglingStories] = useState({});
  const [togglingClips, setTogglingClips] = useState({});

  useEffect(() => {
    const debounce = setTimeout(() => {
      setSearchTerm(searchInput.trim());
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchInput]);

  useEffect(() => {
    if (preferencesAppliedRef.current || userDetailsLoading) {
      return;
    }

    const artSpeaksPrefs = preferences?.artSpeaks || {};
    let applied = false;

    if (artSpeaksPrefs.defaultViewMode && ['grid', 'list'].includes(artSpeaksPrefs.defaultViewMode)) {
      setViewMode(artSpeaksPrefs.defaultViewMode);
      applied = true;
    }

    if (artSpeaksPrefs.defaultContentFilter && ['all', 'story', 'audio'].includes(artSpeaksPrefs.defaultContentFilter)) {
      setContentFilter(artSpeaksPrefs.defaultContentFilter);
      applied = true;
    }

    if (
      artSpeaksPrefs.defaultStoryType &&
      storyTypeOptions.some((option) => option.value === artSpeaksPrefs.defaultStoryType)
    ) {
      setStoryTypeFilter(artSpeaksPrefs.defaultStoryType);
      applied = true;
    }

    if (typeof artSpeaksPrefs.showFeaturedOnly === 'boolean') {
      setFeaturedOnly(artSpeaksPrefs.showFeaturedOnly);
      applied = true;
    }

    if (applied || userDetails) {
      preferencesAppliedRef.current = true;
    }
  }, [preferences, userDetailsLoading, userDetails]);

  const fetchDirectoryContent = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const storyQuery = supabase
        .from('artspeaks_stories')
        .select(`
          story_id,
          title,
          story_text,
          story_type,
          featured,
          visibility,
          created_at,
          updated_at,
          published_at,
          audio_url,
          video_url,
          cover_image,
          vc_id,
          event_id,
          content_warnings,
          story_writer:users!artist_uuid (uuid, nickname, first_name, last_name, handle, profile_img),
          artwork:vcs!vc_id (
            vc_id,
            title,
            cover_image,
            visual_artist:users!uuid (uuid, nickname, first_name, last_name, handle)
          ),
          event:events!event_id (id, name, event_start)
    `)
        .order('created_at', { ascending: false })
        .limit(250);

      const [storyResult, audioResult] = await Promise.all([
        storyQuery,
        supabase.functions.invoke('get_artworks_with_audio', {
          body: {
            page: 1,
            limit: 150,
            search: '',
            sortBy: 'created_at',
            sortOrder: 'desc',
            isPublic: !isAdmin,
            featured: false
          }
        })
      ]);

      if (storyResult.error) throw storyResult.error;

      let audioItems = [];
      if (audioResult.error) {
        console.warn('Audio edge function failed:', audioResult.error);
      } else if (audioResult.data?.success && Array.isArray(audioResult.data.data)) {
        audioItems = audioResult.data.data;
      } else if (Array.isArray(audioResult.data)) {
        audioItems = audioResult.data;
      }

      const normalizedStories = (storyResult.data || []).map(normalizeStory);
      const normalizedAudio = (audioItems || []).map(normalizeAudioArtwork);

      setAllItems([...normalizedStories, ...normalizedAudio]);
    } catch (fetchError) {
      console.error('Art Speaks load error:', fetchError);
      setError(fetchError.message || 'Unable to load Art Speaks content');
      setAllItems([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, refreshKey]);

  useEffect(() => {
    fetchDirectoryContent();
  }, [fetchDirectoryContent]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, contentFilter, storyTypeFilter, featuredOnly]);

  useEffect(() => {
    if (loading) {
      setDisplayItems([]);
      setTotalCount(0);
      return;
    }

    if (!allItems.length) {
      setDisplayItems([]);
      setTotalCount(0);
      return;
    }

    const searchLower = searchTerm.toLowerCase();

    const filtered = allItems.filter((item) => {
      if (contentFilter !== 'all' && item.type !== contentFilter) return false;
      if (featuredOnly) {
        const featuredFlag = item.type === 'story' ? item.featured : item.hasFeaturedAudio;
        if (!featuredFlag) return false;
      }
      if (item.type === 'story' && storyTypeFilter !== 'all' && item.storyType !== storyTypeFilter) {
        return false;
      }

      if (!searchLower) return true;

      const haystack = [
        item.title,
        item.writerName,
        item.visualArtistName,
        item.artworkTitle,
        item.eventName,
        item.storyText,
        item.artistName,
        item.description
      ].filter(Boolean).join(' ').toLowerCase();

      return haystack.includes(searchLower);
    });

    const sorted = filtered.sort((a, b) => {
      let result = 0;
      if (sortBy === 'title') {
        result = (a.title || '').localeCompare(b.title || '');
      } else if (sortBy === 'artist') {
        const artistA = a.type === 'story' ? a.writerName || a.visualArtistName || '' : a.artistName || '';
        const artistB = b.type === 'story' ? b.writerName || b.visualArtistName || '' : b.artistName || '';
        result = artistA.localeCompare(artistB);
      } else {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        result = dateA - dateB;
      }
      return sortOrder === 'asc' ? result : -result;
    });

    const total = sorted.length;
    const maxPage = Math.max(1, Math.ceil(total / ITEMS_PER_PAGE));

    if (currentPage > maxPage) {
      setCurrentPage(1);
      return;
    }

    const offset = (currentPage - 1) * ITEMS_PER_PAGE;
    const pageSlice = sorted.slice(offset, offset + ITEMS_PER_PAGE);

    setDisplayItems(pageSlice);
    setTotalCount(total);
  }, [allItems, loading, searchTerm, contentFilter, storyTypeFilter, featuredOnly, sortBy, sortOrder, currentPage]);

  const registerAudioRef = useCallback((key, ref) => {
    if (ref) {
      audioRefs.current[key] = ref;
    }
  }, []);

  const toggleAudio = useCallback((key) => {
    const audio = audioRefs.current[key];
    if (!audio) return;

    if (playingKey === key) {
      audio.pause();
      setPlayingKey(null);
      return;
    }

    Object.values(audioRefs.current).forEach((player) => {
      if (player && !player.paused) {
        player.pause();
      }
    });

    audio.play().then(() => {
      setPlayingKey(key);
    }).catch((playError) => {
      console.error('Audio play failed:', playError);
    });
  }, [playingKey]);

  const updateAudioProgress = useCallback((key) => {
    const audio = audioRefs.current[key];
    if (!audio) return;
    setAudioProgress((prev) => ({ ...prev, [key]: audio.currentTime }));
  }, []);

  const updateAudioDuration = useCallback((key) => {
    const audio = audioRefs.current[key];
    if (!audio) return;
    setAudioDuration((prev) => ({ ...prev, [key]: audio.duration || 0 }));
  }, []);

  const handleProgressClick = useCallback((key, event) => {
    const audio = audioRefs.current[key];
    if (!audio) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const ratio = clickX / rect.width;
    audio.currentTime = ratio * (audio.duration || 0);
    setAudioProgress((prev) => ({ ...prev, [key]: audio.currentTime }));
  }, []);

  const handleStoryFeaturedToggle = async (storyId) => {
    setTogglingStories((prev) => ({ ...prev, [storyId]: true }));
    try {
      const { error: toggleError } = await toggleStoryFeatured(storyId);
      if (toggleError) throw toggleError;
      setRefreshKey((prev) => prev + 1);
    } catch (toggleErr) {
      console.error('Story feature toggle failed:', toggleErr);
      alert('Unable to update story featured status.');
    } finally {
      setTogglingStories((prev) => ({ ...prev, [storyId]: false }));
    }
  };

  const handleAudioClipToggle = async (clipId, currentFeatured) => {
    setTogglingClips((prev) => ({ ...prev, [clipId]: true }));
    try {
      const { error } = await supabase
        .from('media_elements')
        .update({ featured: !currentFeatured })
        .eq('element_id', clipId);

      if (error) throw error;
      setRefreshKey((prev) => prev + 1);
    } catch (clipError) {
      console.error('Audio clip feature toggle failed:', clipError);
      alert('Unable to update audio clip featured status.');
    } finally {
      setTogglingClips((prev) => ({ ...prev, [clipId]: false }));
    }
  };

  const handleCreateStory = () => {
    navigate('/stories/create');
  };

  const clearFilters = () => {
    setSearchInput('');
    setContentFilter('all');
    setStoryTypeFilter('all');
    setFeaturedOnly(false);
    setSortBy('created_at');
    setSortOrder('desc');
    setCurrentPage(1);
  };

  const paginationControls = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(totalCount / ITEMS_PER_PAGE));
    if (totalPages <= 1) return null;

    const pages = [];
    const visibleCount = 5;
    const startPage = Math.max(1, currentPage - Math.floor(visibleCount / 2));
    const endPage = Math.min(totalPages, startPage + visibleCount - 1);

    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(
        <button
          key={page}
          className={page === currentPage ? 'page-btn active' : 'page-btn'}
          onClick={() => setCurrentPage(page)}
        >
          {page}
        </button>
      );
    }

    return (
      <div className="directory-pagination">
        <button
          className="page-btn"
          onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          disabled={currentPage === 1}
        >
          <ChevronDown className="rotate-90" />
        </button>
        {pages}
        <button
          className="page-btn"
          onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          disabled={currentPage === totalPages}
        >
          <ChevronUp className="rotate-90" />
        </button>
      </div>
    );
  }, [currentPage, totalCount]);

  const renderAudioPreview = (item) => {
    const clips = item.type === 'story'
      ? (item.audioUrl ? [{ source: item.audioUrl, title: 'Story narration' }] : [])
      : item.audioClips?.slice(0, 2) || [];

    if (!clips.length) return null;

    return (
      <div className="audio-stack">
        {clips.map((clip, index) => {
          const clipKey = `${item.id}-${index}`;
          const isPlaying = playingKey === clipKey;
          const current = audioProgress[clipKey] || 0;
          const duration = audioDuration[clipKey] || 0;
          const progressPercent = duration > 0 ? (current / duration) * 100 : 0;

          return (
            <div key={clipKey} className="audio-player-wrapper">
              <div className="audio-player-block">
                <button
                  type="button"
                  className={`pill-button audio-control-btn${isPlaying ? ' playing' : ''}`}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleAudio(clipKey);
                  }}
                >
                  {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                  <span>{isPlaying ? 'Pause' : 'Play'}</span>
                </button>
                <div className="audio-body">
                  <div className="audio-title">{clip.title || (item.type === 'story' ? 'Story Audio' : `Audio Clip ${index + 1}`)}</div>
                  <div
                    className="audio-progress-track"
                    onClick={(event) => {
                      event.stopPropagation();
                      handleProgressClick(clipKey, event);
                    }}
                  >
                    <div className="audio-progress-fill" style={{ width: `${progressPercent}%` }} />
                  </div>
                  <div className="audio-timestamps">
                    {formatTime(current)} / {formatTime(duration)}
                  </div>
                </div>
                <audio
                  ref={(ref) => registerAudioRef(clipKey, ref)}
                  src={clip.source || clip.audio_url || clip.url}
                  onEnded={() => setPlayingKey(null)}
                  onTimeUpdate={() => updateAudioProgress(clipKey)}
                  onLoadedMetadata={() => updateAudioDuration(clipKey)}
                  preload="metadata"
                />
              </div>
              {item.type === 'audio' && clip.element_id && isAdmin && (
                <button
                  type="button"
                  className={`pill-button audio-feature-btn${clip.featured ? ' active' : ''}`}
                  disabled={togglingClips[clip.element_id]}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleAudioClipToggle(clip.element_id, clip.featured);
                  }}
                >
                  {togglingClips[clip.element_id] ? (
                    <RefreshCw size={14} className="spinning" />
                  ) : clip.featured ? (
                    <Star size={14} />
                  ) : (
                    <StarOff size={14} />
                  )}
                  <span>{clip.featured ? 'Featured clip' : 'Feature this clip'}</span>
                </button>
              )}
            </div>
          );
        })}
        {item.type === 'audio' && item.audioCount > clips.length && (
          <div className="more-audio">+{item.audioCount - clips.length} more clip{item.audioCount - clips.length === 1 ? '' : 's'}</div>
        )}
      </div>
    );
  };

  return (
    <div className="art-speaks-directory">
      <div className="hero">
        <div className="hero-copy">
          <div className="hero-label">Art Speaks</div>
          <h1 className="hero-title">Stories and Audio That Bring Art to Life</h1>
          <p className="hero-subtitle">
            Browse published Art Speaks stories alongside audio-enhanced artworks. Administrators can feature content and create new stories directly from this page.
          </p>
          <div className="hero-actions">
            {isAdmin ? (
              <button type="button" className="primary-btn" onClick={handleCreateStory}>
                <Plus size={16} />
                Create Art Story
              </button>
            ) : (
              <span className="action-hint">Sign in as a curator to create new stories.</span>
            )}
            <a
              href="https://www.vibrantcast.com/art-speaks"
              target="_blank"
              rel="noreferrer"
              className="secondary-btn"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
            >
              <ExternalLink size={16} />
              View Published
            </a>
            <button
              type="button"
              className="secondary-btn"
              onClick={() => setRefreshKey((prev) => prev + 1)}
            >
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
        <div className="hero-stats">
          <div className="stat">
            <span className="stat-value">{totalCount}</span>
            <span className="stat-label">Items</span>
          </div>
          <div className="stat">
            <span className="stat-value">{allItems.filter((item) => item.type === 'story').length}</span>
            <span className="stat-label">Stories</span>
          </div>
          <div className="stat">
            <span className="stat-value">{allItems.filter((item) => item.type === 'audio').length}</span>
            <span className="stat-label">Audio Artworks</span>
          </div>
        </div>
      </div>

      <div className="directory-controls">
        <div className="search-bar">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search stories, artworks, artists…"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
          {searchInput && (
            <button type="button" onClick={() => setSearchInput('')} className="clear-search">
              ×
            </button>
          )}
        </div>

        <div className="control-row">
          <select
            value={contentFilter}
            onChange={(event) => setContentFilter(event.target.value)}
          >
            {contentTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <select
            value={storyTypeFilter}
            onChange={(event) => setStoryTypeFilter(event.target.value)}
            disabled={contentFilter === 'audio'}
          >
            {storyTypeOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={featuredOnly}
              onChange={(event) => setFeaturedOnly(event.target.checked)}
            />
            Featured Only
          </label>
          <select
            value={sortBy}
            onChange={(event) => setSortBy(event.target.value)}
          >
            {sortOptions.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <button
            type="button"
            className={`control-pill${sortOrder === 'desc' ? ' active' : ''}`}
            onClick={() => setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))}
            title={sortOrder === 'asc' ? 'Ascending order' : 'Descending order'}
          >
            {sortOrder === 'asc' ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            <span>{sortOrder === 'asc' ? 'Asc' : 'Desc'}</span>
          </button>
          <div className="view-toggle">
            <button
              type="button"
              className={viewMode === 'grid' ? 'active' : ''}
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 size={16} />
            </button>
            <button
              type="button"
              className={viewMode === 'list' ? 'active' : ''}
              onClick={() => setViewMode('list')}
            >
              <List size={16} />
            </button>
          </div>
          <button type="button" className="link-btn" onClick={clearFilters}>
            Clear
          </button>
        </div>
      </div>

      {loading && (
        <div className="loading-state">
          <Loader2 className="spinning" size={22} />
          <span>Loading Art Speaks content…</span>
        </div>
      )}

      {!loading && error && (
        <div className="error-state">
          <p>{error}</p>
          <button type="button" className="primary-btn" onClick={() => setRefreshKey((prev) => prev + 1)}>
            Try Again
          </button>
        </div>
      )}

      {!loading && !error && !displayItems.length && (
        <div className="empty-state">
          <Volume2 size={28} />
          <h3>No matching Art Speaks content</h3>
          <p>Try adjusting your filters or create a new story.</p>
        </div>
      )}

      {!loading && !error && displayItems.length > 0 && (
        <div className={`directory-grid ${viewMode}`}>
          {displayItems.map((item) => {
            const thumbSrc = viewMode === 'list' && item.coverImage && item.coverImage.includes('/upload/')
              ? item.coverImage.replace('/upload/', '/upload/t_square-300/')
              : item.coverImage;
            return (
            <div key={item.id} className={`directory-card type-${item.type}`}>
              <div className="card-media" onClick={() => item.artworkId && navigate(`/artwork/${item.artworkId}`)}>
                {item.coverImage ? (
                  <img src={thumbSrc} alt={item.title} />
                ) : (
                  <div className="media-placeholder">
                    <Volume2 size={28} />
                  </div>
                )}
              </div>

              <div className="card-content">
                <div className="card-badges">
                  <span className="badge">{item.type === 'story' ? 'Art Story' : 'Audio Artwork'}</span>
                  {item.type === 'story' && item.storyType && (
                    <span className="badge neutral">{item.storyType}</span>
                  )}
                  {item.type === 'story' && item.featured && <span className="badge featured">Featured</span>}
                  {item.type === 'audio' && item.hasFeaturedAudio && <span className="badge featured">Featured Audio</span>}
                </div>
                <div className="card-header">
                  <h3>{item.title}</h3>
                  <div className="card-meta">
                    {item.type === 'story' ? (
                      <>
                        {item.writerName && <span>Writer: {item.writerName}</span>}
                        {item.visualArtistName && <span>Artist: {item.visualArtistName}</span>}
                        {item.eventName && <span><Calendar size={12} /> {item.eventName}</span>}
                      </>
                    ) : (
                      <>
                        {item.artistName && <span>Artist: {item.artistName}</span>}
                        {item.audioCount > 0 && <span>{item.audioCount} audio clip{item.audioCount === 1 ? '' : 's'}</span>}
                      </>
                    )}
                  </div>
                </div>

                {item.type === 'story' && item.storyText && (
                  <p className="card-body-text">{truncateText(item.storyText)}</p>
                )}
                {item.type === 'audio' && item.description && (
                  <p className="card-body-text">{truncateText(item.description)}</p>
                )}

                {renderAudioPreview(item)}
              </div>

              <div className="card-footer">
                <div className="card-actions">
                  {item.type === 'story' && isAdmin && (
                    <button
                      type="button"
                      className="action-btn"
                      onClick={() => navigate(`/admin/stories/${item.rawId}/edit`)}
                    >
                      <Edit2 size={16} />
                      Edit Story
                    </button>
                  )}
                </div>
                {item.type === 'story' && isAdmin && (
                  <button
                    type="button"
                    className={`feature-toggle ${item.featured ? 'active' : ''}`}
                    disabled={togglingStories[item.rawId]}
                    onClick={() => handleStoryFeaturedToggle(item.rawId)}
                    title={item.featured ? 'Remove from featured' : 'Mark as featured'}
                  >
                    {togglingStories[item.rawId] ? <RefreshCw size={14} className="spinning" /> : item.featured ? <Star size={14} /> : <StarOff size={14} />}
                  </button>
                )}
              </div>
            </div>
          );
          })}
        </div>
      )}

      {!loading && !error && paginationControls}
    </div>
  );
};

export default ArtSpeaksDirectory;
